import { useEffect, useMemo, useRef, useState } from 'react';
import {
  joinEasyEstimateKosztorys,
  leaveEasyEstimateKosztorys,
  onEasyEstimateKosztorysPresence,
  onEasyEstimateSheetCell,
  type EasyEstimateKosztorysMember,
  type EasyEstimateRemoteSheetCell,
} from '../../services/realtimeService';

function normalizeMember(raw: unknown): EasyEstimateKosztorysMember | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const uid = o.userId != null ? String(o.userId) : '';
  if (!uid) return null;
  return {
    userId: uid,
    displayName: typeof o.displayName === 'string' ? o.displayName : undefined,
    avatarUrl: typeof o.avatarUrl === 'string' ? o.avatarUrl : undefined,
  };
}

/**
 * Obecność przy otwartej zakładce Kosztorys (ten sam estimate).
 * Zwraca wyłącznie **innych** użytkowników (bez bieżącego), z deduplikacją po userId.
 * `remoteSheetCells` — mapa userId → aktywna komórka arkusza (`cellId` stabilny string).
 */
export function useEasyEstimateKosztorysPresence(
  estimateId: number | undefined,
  tabActive: boolean,
  currentUserId: string | undefined,
  displayName: string | undefined,
  avatarUrl: string | undefined,
): {
  othersPresent: EasyEstimateKosztorysMember[];
  remoteSheetCells: Record<string, EasyEstimateRemoteSheetCell>;
} {
  const [members, setMembers] = useState<EasyEstimateKosztorysMember[]>([]);
  const [remoteSheetCells, setRemoteSheetCells] = useState<Record<string, EasyEstimateRemoteSheetCell>>({});
  const displayNameRef = useRef(displayName);
  const avatarUrlRef = useRef(avatarUrl);
  displayNameRef.current = displayName;
  avatarUrlRef.current = avatarUrl;

  const estimateKey = estimateId != null && Number.isFinite(estimateId) ? String(estimateId) : '';

  useEffect(() => {
    if (!estimateKey || !tabActive || !currentUserId) {
      setMembers([]);
      setRemoteSheetCells({});
      return;
    }
    joinEasyEstimateKosztorys(estimateKey, displayNameRef.current, avatarUrlRef.current);

    const pruneByMemberIds = (list: EasyEstimateKosztorysMember[]) => {
      const ids = new Set(list.map((m) => String(m.userId)));
      setRemoteSheetCells((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (!ids.has(k)) delete next[k];
        }
        return next;
      });
    };

    const offPresence = onEasyEstimateKosztorysPresence((p) => {
      if (p.estimateId !== estimateKey) return;
      const list: EasyEstimateKosztorysMember[] = [];
      for (const m of p.members) {
        const n = normalizeMember(m);
        if (n) list.push(n);
      }
      setMembers(list);
      pruneByMemberIds(list);
    });

    const self = String(currentUserId);
    const offSheet = onEasyEstimateSheetCell((p) => {
      if (p.estimateId !== estimateKey) return;
      const uid = String(p.fromUserId);
      if (!uid || uid === self) return;
      setRemoteSheetCells((prev) => {
        const next = { ...prev };
        if (p.cellId == null || p.cellId === '') {
          delete next[uid];
        } else {
          next[uid] = { cellId: p.cellId, displayName: p.displayName };
        }
        return next;
      });
    });

    return () => {
      offPresence();
      offSheet();
      leaveEasyEstimateKosztorys(estimateKey);
      setMembers([]);
      setRemoteSheetCells({});
    };
  }, [estimateKey, tabActive, currentUserId]);

  const othersPresent = useMemo(() => {
    const self = String(currentUserId || '');
    const byId = new Map<string, EasyEstimateKosztorysMember>();
    for (const m of members) {
      if (m.userId === self) continue;
      byId.set(m.userId, m);
    }
    return Array.from(byId.values());
  }, [members, currentUserId]);

  return { othersPresent, remoteSheetCells };
}
